// this is the amalgamation of all of the things needed for marketplace
import { Inject, Injectable } from '@angular/core';
import { Addon } from '@galaxy/marketplace-apps/v1';
import {
  ActivatableApp,
  AppEnablementStatus,
  MarketplaceAppService as VendastaCoreAppService,
  PartnerAppEnablementStatus,
  PartnerAppEnablementStatusService,
} from '@vendasta/marketplace-packages';
import { combineLatest, Observable, of as observableOf, ReplaySubject } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { AppIDs, GET_VENDOR_APP_NAMES_INSTEAD_OF_WHITELABELED_NAMES } from '../constants';
import { AddonsService } from './addons.service';
import { MarketplaceAppService } from './marketplace-app.service';
import { MarketplaceApp } from './marketplace-app/marketplace-app';
import { MarketsService } from './markets/markets.service';
import { ProductService } from './product.service';
import { Product } from './product/product';

export enum AppEnablementStatusesAppEnablementStatus {
  NOT_SPECIFIED = 0,
  ENABLED = 1,
  DISABLED = 2,
}

@Injectable({ providedIn: 'root' })
export class MarketplaceApiService {
  private partnerAppEnablementStatus$$: ReplaySubject<PartnerAppEnablementStatus[]> = new ReplaySubject<
    PartnerAppEnablementStatus[]
  >();
  private activatableApps$$: ReplaySubject<ActivatableApp[]> = new ReplaySubject<ActivatableApp[]>();
  private partnerAppEnablementStatus$: Observable<PartnerAppEnablementStatus[]> =
    this.partnerAppEnablementStatus$$.asObservable();
  private activatableApps$: Observable<ActivatableApp[]> = this.activatableApps$$.asObservable();
  private readonly customProducts$: Observable<Product[]>;

  appsForSelectedMarket$: Observable<MarketplaceApp[]>;
  paesMap$: Observable<{ [key: string]: AppEnablementStatus }>;
  addons$: Observable<Addon[]>;

  ALWAYS_ON_PRODUCTS: string[] = [AppIDs.AdvertisingIntelligence, AppIDs.ListingBuilder];

  constructor(
    private productService?: ProductService,
    private appService?: VendastaCoreAppService,
    private marketplaceAppService?: MarketplaceAppService,
    private addonService?: AddonsService,
    @Inject('PARTNER_ID') private partnerId$?: Observable<string>,
    private marketsService?: MarketsService,
    private paesService?: PartnerAppEnablementStatusService,
  ) {
    this.paesMap$ = this.partnerAppEnablementStatus$.pipe(
      map((paes) => {
        const paesMap: { [key: string]: AppEnablementStatus } = {};
        if (paes) {
          paes.forEach((status) => {
            paesMap[status.appId] = status.status;
          });
        }
        return paesMap;
      }),
      shareReplay(1),
    );

    this.appsForSelectedMarket$ = this.marketplaceAppService.appsForSelectedMarket;

    this.addons$ = this.partnerId$.pipe(
      switchMap((partnerId) => {
        return this.marketplaceAppService.apps$.pipe(
          switchMap((apps) => {
            if (apps?.length > 0) {
              const appIds = apps.map((app) => app.appId);
              return this.getAddonsForAppIds(appIds, partnerId);
            }
            return observableOf([]);
          }),
          catchError(() => {
            return observableOf([]);
          }),
          shareReplay(1),
        );
      }),
    );

    this.customProducts$ = this.productService.products.pipe(
      map((products) => {
        return products ? products.filter((product) => product.product_id.startsWith('CP')) : [];
      }),
    );

    this.loadPartnerAppEnablementStatus();
    this.loadActivatableApps();
  }

  private loadActivatableApps(): void {
    combineLatest([this.partnerId$, this.marketsService.currentMarket$])
      .pipe(
        switchMap(([partnerId, market]) => {
          return this.appService.getAllActivatableApps(
            partnerId,
            market.market_id,
            GET_VENDOR_APP_NAMES_INSTEAD_OF_WHITELABELED_NAMES,
          );
        }),
        take(1),
      )
      .subscribe((activateAbleApps) => this.activatableApps$$.next(activateAbleApps));
  }

  private loadPartnerAppEnablementStatus(): void {
    this.partnerId$
      .pipe(
        switchMap((partnerId) => {
          return this.paesService.listPartnerAppEnablementStatus(partnerId);
        }),
        take(1),
      )
      .subscribe((paes) => this.partnerAppEnablementStatus$$.next(paes));
  }

  public refreshMarketplaceData(): void {
    this.marketplaceAppService.loadAllMarketplaceApps();
    this.loadPartnerAppEnablementStatus();
    this.loadActivatableApps();
    this.productService.loadAllProducts();
    this.productService.loadApprovedProducts();
  }

  /* Switchmap/ CombineLatest / subscribe/ forkJoin to these to get changes. You can also use these directly in templates */
  apps(): Observable<MarketplaceApp[]> {
    return this.marketplaceAppService.apps$;
  }

  activatableApps(): Observable<ActivatableApp[]> {
    return this.activatableApps$;
  }

  // ALL PRODUCTS (including custom products)
  products(): Observable<Product[]> {
    return this.productService.products;
  }

  // ALL PRODUCTS (including custom products) whitelabeled for current market
  productsForMarket(): Observable<Product[]> {
    return this.productService.productsForMarket;
  }

  // A subset of products containing only custom products
  customProducts(): Observable<Product[]> {
    return this.customProducts$;
  }

  // takes a list of all marketplace apps and a list of visibleIds that should be visible for a partner
  // filters out the marketplace apps that should not be visible to the partner
  filterAllowedApps(visibleIds: string[], apps: MarketplaceApp[]): MarketplaceApp[] {
    const allowedApps: MarketplaceApp[] = [];
    for (const app of apps) {
      if (visibleIds.indexOf(app.appId) !== -1) {
        allowedApps.push(app);
      }
    }
    return allowedApps;
  }

  // gets all of the addons associated with the products a partner has
  // this should wrap the addons/addons.service.ts
  getAddonsForAppIds(appIds: string[], partnerId: string): Observable<Addon[]> {
    return this.addonService.listAddonsMulti(appIds, partnerId);
  }

  get paes$(): Observable<PartnerAppEnablementStatus[]> {
    return this.partnerAppEnablementStatus$;
  }
}
